import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  timeZone,
  tripTimeline,
  getTimeline,
  vehicleTripData,
  getVehicleTripData,
} from "store/vehicleTripSlice";
import { tripData } from "store/tripSlice";
import { Box, Paper, Stack, Chip, Typography, Alert, CircularProgress, Button, Tooltip } from "@mui/material";
import {
  Refresh as RefreshIcon,
  GpsFixed as GpsIcon,
  Route as RouteIcon,
  DirectionsCar as DirectionsCarIcon,
  Place as PlaceIcon,
} from "@mui/icons-material";
import moment from "moment";
import "moment-timezone";
import { GetTimeZoneFromAbbreviation } from "common/Constants";
import VehicleHistory from "map/components/VehicleHistory";
import ReconstructionService, {
  ReconstructedEventDto,
  IntermediateStopDto,
  TripReconstructionResponse,
} from "services/reconstructionService";

interface GpsReconstructLayoutProps {
  tripNumber?: string;
  isHidden?: boolean;
}

type ChipColor = "default" | "primary" | "secondary" | "info" | "success" | "warning" | "error";

// The four middle events we rebuild, in canonical order.
const EVENT_ORDER = [
  "Arrived at Pickup",
  "Depart with Crew",
  "Arrived at Destination",
  "Drop off Crew",
];

const GpsReconstructLayout = ({ tripNumber, isHidden }: GpsReconstructLayoutProps) => {
  const dispatch = useDispatch();
  const trip = useSelector(tripData);
  const _timeZone = useSelector(timeZone);
  const _tripTimeline = useSelector(tripTimeline);
  const _vehicleActivity = useSelector(vehicleTripData);

  const [recon, setRecon] = useState<TripReconstructionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A row click flies the map to that GPS event/stop. The nonce lets a repeat click on the
  // same row re-trigger the fly-to (a new object each time changes the prop identity).
  const [focusPoint, setFocusPoint] = useState<{ latitude: number; longitude: number; nonce: number } | undefined>();
  const focusOn = (lat?: number, lng?: number) => {
    if (lat == null || lng == null) return;
    setFocusPoint({ latitude: lat, longitude: lng, nonce: Date.now() });
  };
  // Applied to rows that have a GPS location, so they read as clickable.
  const clickableSx = {
    cursor: "pointer",
    transition: "box-shadow 120ms ease, transform 120ms ease",
    "&:hover": { boxShadow: 4, transform: "translateX(2px)" },
  } as const;

  const tz = useMemo(() => GetTimeZoneFromAbbreviation(_timeZone), [_timeZone]);

  const fmt = (t?: string) =>
    t ? `${moment.utc(t).tz(tz).format("MM/DD/YY HH:mm")} ${_timeZone}` : "—";

  const loadReconstruction = (tripId: number) => {
    setLoading(true);
    setError(null);
    ReconstructionService.reconstructTrip(tripId)
      .then((r) => setRecon(r))
      .catch(() => setError("Could not generate the GPS trip. Please try again."))
      .finally(() => setLoading(false));
  };

  // Pull the driver timeline so the map has its Begin/End window + driver event markers.
  useEffect(() => {
    if (!trip?.id) return;
    dispatch(getTimeline({ tripNumber: trip.id.toString(), isActive: "active" }));
  }, [trip?.id, dispatch]);

  // Pre-fetch the vehicle's GPS activity so the map renders the actual track (mirrors MapEditLayout).
  useEffect(() => {
    if (!trip?.vehicle?.unitNumber || !_tripTimeline?.length) return;

    const startEvent = _tripTimeline.find((t: any) => t.type === "Begin Trip");
    const endEvent = _tripTimeline.find((t: any) => t.type === "End Trip");
    if (!startEvent?.time) return;

    const start = moment.utc(startEvent.time);
    const end = endEvent?.time
      ? moment.utc(endEvent.time)
      : moment.utc(startEvent.time).add(12, "hours");

    dispatch(
      getVehicleTripData({
        unitNumber: +trip.vehicle.unitNumber,
        startDate: start,
        endDate: end,
        timezone: _timeZone,
        tripNumber: trip.tripNumber,
        clientName: trip.clientName,
      })
    );
  }, [trip?.vehicle?.unitNumber, _tripTimeline, _timeZone, dispatch]);

  // Generate the reconstruction once the trip is known.
  useEffect(() => {
    if (!trip?.id) return;
    loadReconstruction(trip.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.id]);

  // Order events canonically; keep any unexpected extras at the end.
  const orderedEvents: ReconstructedEventDto[] = useMemo(() => {
    const events = recon?.events ?? [];
    return [...events].sort(
      (a, b) => EVENT_ORDER.indexOf(a.eventTypeName) - EVENT_ORDER.indexOf(b.eventTypeName)
    );
  }, [recon]);

  // One chronological list: the 4 role events interleaved with the intermediate stops,
  // sorted by their GPS time, so a fuel stop or inspection shows up where it actually
  // happened instead of dumped at the bottom.
  type TimelineRow =
    | { kind: "event"; ev: ReconstructedEventDto; t: number }
    | { kind: "stop"; stop: IntermediateStopDto; t: number };
  const timeline: TimelineRow[] = useMemo(() => {
    const ms = (s?: string) => (s ? moment.utc(s).valueOf() : Number.MAX_SAFE_INTEGER);
    const rows: TimelineRow[] = [
      ...orderedEvents.map(
        (ev): TimelineRow => ({ kind: "event", ev, t: ms(ev.suggestedTimeUtc ?? ev.currentTimeUtc) })
      ),
      ...(recon?.intermediateStops ?? []).map(
        (stop): TimelineRow => ({ kind: "stop", stop, t: ms(stop.startUtc) })
      ),
    ];
    return rows.sort((a, b) => a.t - b.t);
  }, [orderedEvents, recon]);

  // Sequential numbers for the rows that have a GPS location, in the order they render. Each number
  // is drawn both as a badge on the card and as a marker on the map, so they line up 1:1. Rows with no
  // GPS point (interpolated estimates) get no number — there's no marker to point at.
  const numberedPoints = useMemo(() => {
    const pts: Array<{ key: string; latitude: number; longitude: number; label: number }> = [];
    let n = 0;
    timeline.forEach((row, idx) => {
      if (row.kind === "event") {
        const e = row.ev;
        if (e.latitude != null && e.longitude != null) {
          pts.push({ key: `ev-${e.role}`, latitude: e.latitude, longitude: e.longitude, label: ++n });
        }
      } else {
        const st = row.stop;
        if (st.latitude != null && st.longitude != null) {
          pts.push({ key: `stop-${st.startUtc}-${idx}`, latitude: st.latitude, longitude: st.longitude, label: ++n });
        }
      }
    });
    const te = recon?.tripEnding;
    if (te?.lastKnownLatitude != null && te?.lastKnownLongitude != null) {
      pts.push({ key: "trip-ending", latitude: te.lastKnownLatitude, longitude: te.lastKnownLongitude, label: ++n });
    }
    return pts;
  }, [timeline, recon]);
  const numberByKey = useMemo(() => new Map(numberedPoints.map((p) => [p.key, p.label])), [numberedPoints]);

  // Map markers, fanned out where points share a location so the number discs don't stack on top of
  // each other (e.g. Arrive + Depart on the same pickup coordinate). Coincident points get spread on a
  // small ring around their shared spot; the cards keep the true numbers and click-to-zoom the true coord.
  const markerPoints = useMemo(() => {
    const groups = new Map<string, typeof numberedPoints>();
    numberedPoints.forEach((p) => {
      // ~11m grid: points this close would visually collide, so treat them as one cluster to fan.
      const key = `${p.latitude.toFixed(4)},${p.longitude.toFixed(4)}`;
      const g = groups.get(key) ?? [];
      g.push(p);
      groups.set(key, g);
    });

    const out: Array<{ latitude: number; longitude: number; label: number }> = [];
    groups.forEach((g) => {
      if (g.length === 1) {
        out.push({ latitude: g[0].latitude, longitude: g[0].longitude, label: g[0].label });
        return;
      }
      const clat = g.reduce((a, p) => a + p.latitude, 0) / g.length;
      const clng = g.reduce((a, p) => a + p.longitude, 0) / g.length;
      // Ring radius that keeps adjacent discs ~30m apart (readable at street zoom).
      const radius = Math.max(0.00016, 0.00028 / (2 * Math.sin(Math.PI / g.length)));
      const lngScale = Math.cos((clat * Math.PI) / 180) || 1;
      g.forEach((p, i) => {
        const angle = (2 * Math.PI * i) / g.length - Math.PI / 2; // first one at the top
        out.push({
          latitude: clat + radius * Math.sin(angle),
          longitude: clng + (radius * Math.cos(angle)) / lngScale,
          label: p.label,
        });
      });
    });
    return out;
  }, [numberedPoints]);

  // A small filled disc showing the card's number, colored to match the map marker.
  const numberBadge = (n?: number) =>
    n == null ? null : (
      <Box
        sx={{
          minWidth: 22,
          height: 22,
          px: 0.5,
          borderRadius: "50%",
          bgcolor: "#1565c0",
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: "bold",
          mr: 1,
          flexShrink: 0,
        }}
      >
        {n}
      </Box>
    );

  const evidence = (e: ReconstructedEventDto) => {
    if (e.method === "Interpolated") return null;
    const parts: string[] = [];
    if (e.dwellMinutes != null) parts.push(`stopped ${e.dwellMinutes} min`);
    if (e.distanceToPointMiles != null)
      parts.push(`${e.distanceToPointMiles.toFixed(2)} mi from point`);
    if (parts.length === 0) return null;
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        GPS: {parts.join(", ")}
      </Typography>
    );
  };

  // True when the event was placed from learned route history (no GPS) rather than guessed.
  const isFromRouteHistory = (e: ReconstructedEventDto) =>
    e.method === "Interpolated" && (e.note?.toLowerCase().includes("route history") ?? false);

  // How this event's time was established — purely about the GPS source, never a comparison
  // to what the driver entered (the trip is rebuilt from GPS; driver data isn't shown).
  const eventStyle = (e: ReconstructedEventDto) => {
    if (e.isGpsMatched)
      return {
        border: "success.main",
        bg: "rgba(76,175,80,0.06)",
        chipColor: "success" as const,
        chipLabel: "From GPS stop",
        timeLabel: "GPS time",
        hasGpsTime: true,
      };
    if (e.method === "OnSiteAtBoundary")
      return {
        border: "warning.main",
        bg: "rgba(255,167,38,0.10)",
        chipColor: "warning" as const,
        chipLabel: "Parked on-site — arrival = departure",
        timeLabel: "GPS time",
        hasGpsTime: true,
      };
    // No stop was logged at the destination, but the GPS track shows the van turned around here
    // (a down-and-back). Placed from that turnaround instead of a guess.
    if (e.method === "Turnaround")
      return {
        border: "info.main",
        bg: "rgba(2,136,209,0.07)",
        chipColor: "info" as const,
        chipLabel: "Destination from GPS U-turn",
        timeLabel: "GPS turnaround",
        hasGpsTime: true,
      };
    // No GPS stop for this event — placed from the route's history when we have a trustworthy
    // lane, otherwise a plain interpolation. Label it honestly so it never claims "GPS".
    if (isFromRouteHistory(e))
      return {
        border: "secondary.main",
        bg: "rgba(123,31,162,0.06)",
        chipColor: "secondary" as const,
        chipLabel: "Estimated from route history",
        timeLabel: "Route estimate",
        hasGpsTime: true,
      };
    const tripHasGps = recon?.usedGps ?? false;
    return {
      border: "grey.400",
      bg: "rgba(0,0,0,0.03)",
      chipColor: "default" as const,
      chipLabel: tripHasGps ? "No GPS stop here — estimated" : "No GPS — estimated",
      timeLabel: "Estimate",
      hasGpsTime: false,
    };
  };

  // Friendly label + chip color per intermediate-stop category.
  const categoryStyle = (category: string): { label: string; color: ChipColor } => {
    switch (category) {
      case "FuelStop":
        return { label: "Fuel stop", color: "warning" };
      case "CrewWait":
        return { label: "Crew wait", color: "info" };
      case "DriverBreak":
        return { label: "Driver break", color: "default" };
      case "Inspection":
        return { label: "Vehicle inspection", color: "secondary" };
      case "AssistMovement":
        return { label: "Assist movement", color: "primary" };
      case "Staging":
        return { label: "Parked at yard/depot", color: "success" };
      case "OvernightPark":
        return { label: "Overnight / off-shift park", color: "default" };
      default:
        return { label: "Unclassified stop", color: "default" };
    }
  };

  const stopDuration = (s: IntermediateStopDto) => {
    const mins = Math.round(s.dwellMinutes);
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  // The dispatched location a role event happened at: pickup for the pickup-side roles, dropoff for the
  // dropoff-side roles. Name is the point (e.g. "Amarillo TX"); address is the specific one from the notes.
  const isPickupRole = (role: string) => role === "ArrivedAtPickup" || role === "DepartWithCrew";
  const isDropoffRole = (role: string) => role === "ArrivedAtDestination" || role === "DropOffCrew";
  const roleLocationName = (role: string): string | null =>
    isPickupRole(role) ? trip?.pickupLocation?.name ?? null : isDropoffRole(role) ? trip?.dropoffLocation?.name ?? null : null;
  const roleLocationAddress = (role: string): string | null =>
    isPickupRole(role) ? recon?.pickupAddressFromNotes ?? null : isDropoffRole(role) ? recon?.dropoffAddressFromNotes ?? null : null;

  // Whole-trip assist verdict. The trip's DECLARED intent (assist type / estimated assist
  // hours) leads — GPS only corroborates and measures. An unmarked run that GPS reads as an
  // assist is surfaced too, as a softer "review this" flag. Suggestion-only.
  const assistBanner = () => {
    const a = recon?.assist;
    if (!a || (!a.declaredAssist && !a.isAssistPattern)) return null;
    const severity = a.declaredAssist ? "info" : "warning";
    const headline = a.declaredAssist
      ? a.estAssistHours > 0
        ? `Assist — dispatched as a ~${a.estAssistHours} hr assist`
        : "Assist — flagged on the trip"
      : "GPS suggests an unmarked assist";
    const metrics = [
      `${a.stopCount} stop${a.stopCount === 1 ? "" : "s"} along the line`,
      `${a.spanMiles.toFixed(1)} mi GPS span`,
    ];
    if (a.assistMiles > 0) metrics.push(`${a.assistMiles.toFixed(1)} mi recorded`);
    return (
      <Alert severity={severity} icon={<RouteIcon fontSize="inherit" />} sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {headline}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
          {metrics.join(" · ")}
        </Typography>
        {a.note && (
          <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {a.note}
          </Typography>
        )}
      </Alert>
    );
  };

  const gridPanel = (
    <Box sx={{ height: "100%", overflow: "auto", p: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <GpsIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            Trip rebuilt from GPS
          </Typography>
        </Stack>
        <Tooltip title="Regenerate from GPS">
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => trip?.id && loadReconstruction(trip.id)}
              disabled={loading || !trip?.id}
            >
              Regenerate
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
        The trip as the truck actually drove it, rebuilt from the vehicle's GPS. Click any row to zoom the map to that spot.
      </Typography>

      {!loading && recon && (recon.pickupAddressFromNotes || recon.dropoffAddressFromNotes) && (
        <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: "rgba(2,136,209,0.04)" }}>
          <Typography variant="caption" sx={{ fontWeight: "bold", display: "block" }}>
            Actual addresses from dispatch notes
          </Typography>
          {recon.pickupAddressFromNotes && (
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
              Pickup: {recon.pickupAddressFromNotes}
            </Typography>
          )}
          {recon.dropoffAddressFromNotes && (
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
              Drop-off: {recon.dropoffAddressFromNotes}
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: "block", color: "text.disabled", fontStyle: "italic" }}>
            Compare against the ordered points — a mismatch means the actual stop differs from what was entered.
          </Typography>
        </Paper>
      )}

      {loading && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Generating from GPS…</Typography>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {!loading && recon && recon.vehicleMismatch && (
        <Alert severity="warning" icon={<DirectionsCarIcon fontSize="inherit" />} sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Wrong vehicle? GPS doesn't match this trip
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            {recon.vehicleMismatchNote ||
              "The assigned vehicle's GPS never went near the pickup or drop-off — likely a different van or a subcontractor."}
          </Typography>
        </Alert>
      )}

      {!loading && recon && recon.destinationNotReached && (
        <Alert severity="warning" icon={<PlaceIcon fontSize="inherit" />} sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Van never reached the destination
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            {recon.destinationNotReachedNote ||
              "GPS shows the van turned around before reaching the dispatched drop-off — the arrival is an estimate, not GPS-placed."}
          </Typography>
        </Alert>
      )}

      {!loading && recon && !recon.usedGps && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {recon.note || "No GPS stops matched this trip — nothing to suggest."}
        </Alert>
      )}

      {!loading && recon && assistBanner()}

      {!loading && recon?.prePickupOrigin && (
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 1, fontStyle: "italic" }}>
          {recon.prePickupOrigin.milesFromPickup < 0.2
            ? `Before pickup — van was already at the pickup for ${recon.prePickupOrigin.dwellMinutes} min.`
            : `Before pickup — van was parked ${recon.prePickupOrigin.milesFromPickup} mi away for ${recon.prePickupOrigin.dwellMinutes} min, then departed ${fmt(recon.prePickupOrigin.departedUtc)} for the pickup.`}
        </Typography>
      )}

      {!loading &&
        recon &&
        timeline.map((row, idx) => {
          if (row.kind === "event") {
            const e = row.ev;
            const s = eventStyle(e);
            const hasLoc = e.latitude != null && e.longitude != null;
            return (
              <Paper
                key={`ev-${e.role}`}
                onClick={hasLoc ? () => focusOn(e.latitude, e.longitude) : undefined}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderLeft: "4px solid",
                  borderLeftColor: s.border,
                  bgcolor: s.bg,
                  ...(hasLoc ? clickableSx : {}),
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Stack direction="row" alignItems="center">
                    {numberBadge(numberByKey.get(`ev-${e.role}`))}
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {e.eventTypeName}
                    </Typography>
                  </Stack>
                  <Chip size="small" variant="outlined" color={s.chipColor} label={s.chipLabel} />
                </Stack>

                {roleLocationName(e.role) && (
                  <Box sx={{ mb: 0.5, display: "flex", alignItems: "baseline", gap: 0.5 }}>
                    <RouteIcon fontSize="inherit" sx={{ color: "text.secondary", position: "relative", top: 2 }} />
                    <Box>
                      <Typography variant="body2">{roleLocationName(e.role)}</Typography>
                      {roleLocationAddress(e.role) && roleLocationAddress(e.role) !== roleLocationName(e.role) && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {roleLocationAddress(e.role)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {s.timeLabel}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: s.hasGpsTime ? "bold" : "normal",
                      fontStyle: s.hasGpsTime ? "normal" : "italic",
                    }}
                  >
                    {fmt(e.suggestedTimeUtc)}
                  </Typography>
                </Box>

                <Stack direction="row" alignItems="center" spacing={1}>
                  {evidence(e)}
                </Stack>

                {e.note && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.5, color: "warning.dark", fontStyle: "italic" }}
                  >
                    {e.note}
                  </Typography>
                )}
              </Paper>
            );
          }

          const s = row.stop;
          const c = categoryStyle(s.category);
          const hasLoc = s.latitude != null && s.longitude != null;
          return (
            <Paper
              key={`stop-${s.startUtc}-${idx}`}
              onClick={hasLoc ? () => focusOn(s.latitude, s.longitude) : undefined}
              sx={{
                p: 1.5,
                mb: 1,
                borderLeft: "4px solid",
                borderLeftColor: "grey.300",
                ...(hasLoc ? clickableSx : {}),
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Stack direction="row" alignItems="center">
                  {numberBadge(numberByKey.get(`stop-${s.startUtc}-${idx}`))}
                  <Chip size="small" color={c.color} label={c.label} />
                </Stack>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {fmt(s.startUtc)} → {fmt(s.endUtc)}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 0.25 }}>
                Stopped {stopDuration(s)}
                {s.poiName ? ` · near ${s.poiName}` : ""}
                {s.distanceToPointMiles != null ? ` · ${s.distanceToPointMiles.toFixed(2)} mi from point` : ""}
              </Typography>
              {s.note && (
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  {s.note}
                </Typography>
              )}
            </Paper>
          );
        })}
      {recon?.tripEnding && (
        <Paper
          onClick={
            recon.tripEnding.lastKnownLatitude != null && recon.tripEnding.lastKnownLongitude != null
              ? () => focusOn(recon.tripEnding?.lastKnownLatitude, recon.tripEnding?.lastKnownLongitude)
              : undefined
          }
          sx={{
            p: 1.5,
            mb: 1,
            borderLeft: "4px solid",
            borderLeftColor: recon.tripEnding.returnedToStart ? "success.main" : "warning.main",
            ...(recon.tripEnding.lastKnownLatitude != null && recon.tripEnding.lastKnownLongitude != null
              ? clickableSx
              : {}),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Stack direction="row" alignItems="center">
              {numberBadge(numberByKey.get("trip-ending"))}
              <Chip
                size="small"
                color={recon.tripEnding.returnedToStart ? "success" : "warning"}
                label={recon.tripEnding.returnedToStart ? "End Trip · returned to start" : "End Trip · did not return"}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {fmt(recon.tripEnding.returnedToStart ? recon.tripEnding.arrivedBackUtc : recon.tripEnding.lastKnownUtc)}
            </Typography>
          </Stack>
          <Typography variant="body2">{recon.tripEnding.note}</Typography>
        </Paper>
      )}
    </Box>
  );

  return (
    <Box>
      <VehicleHistory
        tripNumber={tripNumber}
        isHidden={isHidden}
        sourceIdPrefix="gps-recon-tab"
        rightPanelContent={gridPanel}
        editable={false}
        hideDriverEvents
        focusPoint={focusPoint}
        numberedMarkers={markerPoints}
      />
    </Box>
  );
};

export default GpsReconstructLayout;
